import { Spinner } from "@blueprintjs/core"
import { ActualPaymentSnapshotType } from "modules/agents/outsourcers/models/outsourcer-payment"
import { Money } from "modules/common/components/money/Money"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { PlanrPopover } from "modules/common/components/planr/popover/Popover"
import { SpendingPaymentInfo } from "modules/orders-manage/models/spending-payment-info"
import React, { useCallback, useState } from "react"
import { SpendingSaver } from "../spending-saver"
import styles from "./ActualPaymentMark.module.scss"

interface ActualPaymentMarkProps {
  /** фактическая оплата */
  actual: ActualPaymentSnapshotType
  /** сумма планового платежа, который она закрывает; нет — оплата без плана */
  planSum?: number
  spendingId: string
  /** все фактические оплаты строки: подробности грузятся по строке целиком */
  actualGuids: string[]
  saver: SpendingSaver
}

/**
 * Строка факта внизу карточки планового платежа: дата и сумма оплаты, под ней корректировки. Подробности (платёжка из выписки, договор,
 * раздел) грузятся при первом наведении одним запросом на строку затрат
 */
export const ActualPaymentMark: React.FC<ActualPaymentMarkProps> = ({
  actual,
  planSum,
  spendingId,
  actualGuids,
  saver,
}) => {
  const [info, setInfo] = useState<SpendingPaymentInfo | null>(null)
  const [state, setState] = useState<"idle" | "loading" | "failed">("idle")

  const load = useCallback(async () => {
    if (info || state === "loading") {
      return
    }

    setState("loading")
    const result = await saver.paymentsInfo(spendingId, actualGuids)
    const found = result?.find((r) => r.guid === actual.guid) ?? null

    setInfo(found)
    setState(found ? "idle" : "failed")
  }, [info, state, saver, spendingId, actualGuids, actual.guid])

  const corrections = actual.correctionPayments ?? []
  const differs = planSum !== undefined && Math.abs(+actual.sum - +planSum) >= 0.01

  return (
    <PlanrPopover
      interactionKind="hover"
      hoverOpenDelay={250}
      placement="right"
      onOpening={load}
      className={styles.target}
      content={<ActualPaymentDetails actual={actual} info={info} state={state} />}
    >
      <div className={styles.fact} data-object-id={actual.guid} onMouseEnter={load}>
        <div className={styles.line}>
          <GeneralIcon type="general-check" size={12} className={styles.icon} />
          <span className={styles.caption}>Оплачено {actual.day}</span>
          <Money amount={+actual.sum} className={differs ? styles.differs : ""} />
          <GeneralIcon type="general-info" size={12} className={styles.info} />
        </div>
        {corrections.map((c) => (
          <div className={`${styles.line} ${styles.correction}`} key={c.guid}>
            <span className={styles.icon} />
            <span className={styles.caption}>Корректировка {c.day}</span>
            <Money amount={-c.sum} />
            <span className={styles.info} />
          </div>
        ))}
      </div>
    </PlanrPopover>
  )
}

interface DetailsProps {
  actual: ActualPaymentSnapshotType
  info: SpendingPaymentInfo | null
  state: "idle" | "loading" | "failed"
}

const Row: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => {
  if (children === null || children === undefined || children === "") {
    return null
  }

  return (
    <tr>
      <td className={styles.label}>{label}</td>
      <td>{children}</td>
    </tr>
  )
}

const ActualPaymentDetails: React.FC<DetailsProps> = ({ actual, info, state }) => {
  const corrections = info?.corrections ?? actual.correctionPayments ?? []
  const bank = info?.bank
  // при разноске выписки в комментарий оплаты попадает назначение платежа — не повторяем его
  const comment = info?.comment || actual.comment || ""
  const ownComment = bank && comment.trim() === bank.description.trim() ? "" : comment

  return (
    <div className={styles.details}>
      <div className={styles.title}>Фактическая оплата</div>
      <table className={styles.table}>
        <tbody>
          <Row label="Дата">{actual.day}</Row>
          <Row label="Сумма">
            <Money amount={+actual.sum} />
            {actual.isCash ? " (наличными)" : ""}
          </Row>
          <Row label="Комментарий">{ownComment}</Row>
          {info && (
            <>
              <Row label="№ договора">{info.orderNumber}</Row>
              <Row label="Раздел">{info.contentName}</Row>
              <Row label="Вид работ">{info.workTypeName}</Row>
              <Row label="Примечание к затрате">{info.spendingComment}</Row>
              <Row label="Провёл">{info.employerName}</Row>
            </>
          )}
        </tbody>
      </table>

      {corrections.length > 0 && (
        <>
          <div className={styles.title}>Корректировки</div>
          <table className={styles.table}>
            <tbody>
              {corrections.map((c, index) => (
                <tr key={index}>
                  <td className={styles.label}>{c.day}</td>
                  <td>
                    <Money amount={-c.sum} />
                    {c.comment ? ` — ${c.comment}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {state === "loading" && (
        <div className={styles.loading}>
          <Spinner size={16} /> Загружаем платёжное поручение…
        </div>
      )}
      {state === "failed" && <div className={styles.muted}>Не удалось загрузить подробности</div>}

      {info && !bank && <div className={styles.muted}>Платёжного поручения из банковской выписки нет</div>}
      {bank && (
        <>
          <div className={styles.title}>
            {bank.documentType || "Платёжное поручение"}
            {bank.number ? ` № ${bank.number}` : ""} от {bank.day}
          </div>
          <table className={styles.table}>
            <tbody>
              <Row label="Сумма">
                <Money amount={+bank.sum} />
              </Row>
              <Row label="Банк">{bank.bankName}</Row>
              <Row label="Плательщик">{bank.payer}</Row>
              <Row label="Получатель">{bank.recipient}</Row>
              <Row label="ИНН получателя">{bank.recipientInn}</Row>
              <Row label="Счёт получателя">
                {bank.recipientAccount}
                {bank.recipientBank ? `, ${bank.recipientBank}` : ""}
              </Row>
              <Row label="Назначение">{bank.description}</Row>
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
